import { useMemo, useState } from "react";

import { type SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  type GetOrganizationsOutput,
  useBasecampOrganizationsRetrieve,
  useListTemplates,
} from "@api/rest/types-and-hooks";
import { humanizeEnum } from "@pages/QARules/qaRuleLabels";

const TEMPLATE_PAGE_SIZE = 50;

interface QAPickerOptions {
  /** Fetch the org list (skip it when the org is fixed or the choices are given). */
  needOrganizations: boolean;
  /** Fetch the template list (skip it when the template is fixed). */
  needTemplates: boolean;
  /** Limit templates to this org's. */
  templateOrganizationId?: number;
}

/** Option lists for the org / template pickers on the QA rules pages. */
export const useQAPickerOptions = ({ needOrganizations, needTemplates, templateOrganizationId }: QAPickerOptions) => {
  const [templateSearch, setTemplateSearch] = useState("");

  const { data: orgData, isLoading: orgsLoading } = useBasecampOrganizationsRetrieve(undefined, {
    query: { enabled: needOrganizations },
  });
  const { data: templateData, isFetching: templatesLoading } = useListTemplates(
    {
      search: templateSearch || undefined,
      exclude_archived: true,
      page_size: TEMPLATE_PAGE_SIZE,
      organization_id: templateOrganizationId,
    },
    { query: { enabled: needTemplates } },
  );

  const organizationItems = useMemo<SelectItem[]>(() => {
    if (orgData?.status !== 200) return [];
    const orgs: GetOrganizationsOutput[] = Array.isArray(orgData.data) ? orgData.data : [orgData.data];
    return orgs
      .map((org) => ({ id: String(org.id), primary: org.name }))
      .sort((a, b) => a.primary.localeCompare(b.primary));
  }, [orgData]);

  const templateItems = useMemo<SelectItem[]>(
    () =>
      templateData?.status === 200
        ? templateData.data.results.map((template) => ({
            id: String(template.id),
            primary: template.name,
            secondary: `${humanizeEnum(template.report_type)} · #${template.id}`,
          }))
        : [],
    [templateData],
  );

  return {
    organizationItems,
    orgsLoading,
    templateItems,
    templatesLoading,
    setTemplateSearch,
  };
};
